import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BookOpen, CheckCircle2, Database, Lock, XCircle } from 'lucide-react';
import { api } from '../../lib/api.js';
import {
  Badge,
  Button,
  Callout,
  Field,
  Input,
  Panel,
  Spinner,
  Switch,
  Tooltip,
  cn,
} from '../../ui/index.js';
import { useConfigWrite } from './useConfigWrite.js';

/**
 * Where this group's knowledge articles get published (FR-VEC-01..04).
 *
 * The screen is built around the one mistake that is expensive here. A
 * *source* is the unit a freshness sweep operates on: publishing a batch
 * replaces everything in that source. Two groups pointed at the same source
 * therefore delete each other's articles on the next sync, and the RAG server
 * cannot tell — from its side both pushes are perfectly well-formed. So the
 * names are presented as the consequential setting they are, not as two more
 * text boxes.
 */

interface Lane {
  enabled: boolean;
  collection: string;
  source: string;
}

interface Knowledge {
  external: Lane;
  internal: Lane;
  reviewAfterDays: number;
  staleGraceDays: number;
  miningEveryDays: number;
  miningLookbackDays: number;
  /** Undefined means "use the deployment default", not zero. */
  suggestMinPercent?: number;
  /** Undefined means "same as agents". */
  requesterSuggestMinPercent?: number;
  suggestJudgeMarginPercent?: number;
  requesterSuggestJudgeMarginPercent?: number;
}

interface Payload {
  knowledge: Knowledge;
  /** What a blank field means. */
  defaults: { suggestMinPercent: number; judgeMarginPercent: number };
  server: {
    configured: boolean;
    canPush: boolean;
    canQuery: boolean;
    environment: string;
    maxBatch: number;
  };
}

const LANES = [
  {
    key: 'external' as const,
    title: 'Requester-facing',
    icon: <BookOpen />,
    blurb:
      'The intake assistant answers requesters from this lane. Articles here must be safe to follow without an agent watching.',
  },
  {
    key: 'internal' as const,
    title: 'Agent-facing',
    icon: <Lock />,
    blurb:
      'Suggested solutions shown to agents on a ticket. May contain internal detail. The assistant never reads this lane.',
  },
];

export function KnowledgeTab({ tenant }: { tenant: string }) {
  const write = useConfigWrite(tenant);
  const q = useQuery({
    queryKey: ['admin-knowledge', tenant],
    queryFn: () => api.get<Payload>(`/api/v1/tenants/${tenant}/admin/knowledge`),
    enabled: Boolean(tenant),
  });

  const [draft, setDraft] = useState<Knowledge | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data.knowledge);
  }, [q.data]);

  if (q.isLoading || !draft) return <Spinner />;
  if (q.error) return <p className="text-xs text-ink-tertiary">The configuration could not be loaded.</p>;

  const server = q.data!.server;
  const saved = q.data!.knowledge;
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const clash =
    `${draft.external.collection}/${draft.external.source}` ===
    `${draft.internal.collection}/${draft.internal.source}`;

  const setLane = (k: 'external' | 'internal', patch: Partial<Lane>) =>
    setDraft((d) => (d ? { ...d, [k]: { ...d[k], ...patch } } : d));

  const save = () => {
    void write.mutateAsync({
      method: 'patch',
      path: `/api/v1/tenants/${tenant}/admin/settings`,
      body: { knowledge: draft },
      done: 'Knowledge publishing saved',
    });
  };

  return (
    <div className="space-y-4">
      <Panel
        title="Knowledge publishing"
        subtitle="Where this group's approved articles are indexed for retrieval"
        action={
          <Button onClick={save} disabled={!dirty || clash} loading={write.isPending}>
            Save
          </Button>
        }
        footer={
          <div className="space-y-1.5 text-2xs leading-relaxed text-ink-tertiary">
            <p>
              A <strong className="text-ink-secondary">collection</strong> is a separate store —
              the requester-facing and agent-facing lanes are different collections so that a
              retrieval which forgets to filter still cannot return agent-only content. A{' '}
              <strong className="text-ink-secondary">source</strong> is this group&rsquo;s
              namespace inside one, and it is what a sync replaces.
            </p>
            <p>
              Nothing is published from here. Only approved articles are ever indexed, and a
              group with both lanes switched off still drafts and reviews normally.
            </p>
          </div>
        }
      >
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          {server.configured ? (
            <>
              <Badge tone="ok" icon={<CheckCircle2 />}>
                server configured
              </Badge>
              <Badge tone={server.canPush ? 'ok' : 'warn'} icon={<Database />}>
                {server.canPush ? 'can publish' : 'no push credential'}
              </Badge>
              <Badge tone={server.canQuery ? 'ok' : 'neutral'}>
                {server.canQuery ? 'can retrieve' : 'no query credential'}
              </Badge>
              <Tooltip content="The environment suffix the RAG server appends to every collection name.">
                <span>
                  <Badge tone="info">{server.environment}</Badge>
                </span>
              </Tooltip>
            </>
          ) : (
            <Badge tone="neutral" icon={<XCircle />}>
              no RAG server configured — set RAG_BASE_URL
            </Badge>
          )}
        </div>

        {!server.configured && (
          <Callout tone="warn" icon={<AlertTriangle />} className="mb-4">
            These names can be set now, but nothing will be published until the server is
            configured. That is deliberate: the names are group configuration, the server
            address and its credentials are deployment configuration, and neither belongs in
            the other.
          </Callout>
        )}

        <div className="space-y-3">
          {LANES.map((l) => {
            const lane = draft[l.key];
            return (
              <div key={l.key} className="rounded-md border border-line-subtle bg-surface-sunken/40 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
                    <span aria-hidden className="[&>svg]:h-3.5 [&>svg]:w-3.5 text-ink-tertiary">
                      {l.icon}
                    </span>
                    {l.title}
                  </span>
                  <span className="ml-auto">
                    <Switch
                      checked={lane.enabled}
                      onCheckedChange={(v) => setLane(l.key, { enabled: v })}
                      label="Publish"
                    />
                  </span>
                </div>
                <p className="mt-1 text-2xs leading-relaxed text-ink-tertiary">{l.blurb}</p>

                <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2">
                  <Field label="Collection" hint="The store. Shared with other groups.">
                    <Input
                      value={lane.collection}
                      onChange={(e) => setLane(l.key, { collection: e.target.value })}
                      className="font-mono"
                      aria-label={`${l.title} collection`}
                    />
                  </Field>
                  <Field
                    label="Source"
                    hint="This group's namespace. Must be unique."
                    error={clash && l.key === 'internal' ? 'Must differ from the other lane' : undefined}
                  >
                    <Input
                      value={lane.source}
                      onChange={(e) => setLane(l.key, { source: e.target.value })}
                      className="font-mono"
                      aria-label={`${l.title} source`}
                    />
                  </Field>
                </div>

                <p className="mt-2 text-3xs text-ink-tertiary">
                  publishes to{' '}
                  <code
                    className={cn(
                      'rounded-xs px-1 font-mono',
                      clash ? 'bg-danger-tint text-danger' : 'bg-surface',
                    )}
                  >
                    {lane.collection}/{lane.source}
                  </code>
                </p>
              </div>
            );
          })}
        </div>

        {clash && (
          <Callout tone="danger" icon={<AlertTriangle />} className="mt-3">
            Both lanes point at the same place. A sync of either one would delete the
            other&rsquo;s articles, and agent-only content would become retrievable by the
            requester-facing assistant.
          </Callout>
        )}

        <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
          <Field label="Review an article again after" hint="Days. Past this it is flagged for review.">
            <Input
              type="number"
              value={String(draft.reviewAfterDays)}
              onChange={(e) => setDraft({ ...draft, reviewAfterDays: Number(e.target.value) })}
              aria-label="Review after days"
            />
          </Field>
          <Field
            label="Grace period before demotion"
            hint="Days past review. Then it leaves the requester-facing lane."
          >
            <Input
              type="number"
              value={String(draft.staleGraceDays)}
              onChange={(e) => setDraft({ ...draft, staleGraceDays: Number(e.target.value) })}
              aria-label="Stale grace days"
            />
          </Field>
          {/* The cadence belongs to the desk, not the deployment — a desk
              taking forty tickets a day has something new to find daily, and
              one taking four would pay to be told yesterday's patterns
              again. Switching mining on or off is on the Features tab; this
              is only how often, and it does nothing while it is off. */}
          <Field
            label="Look for new patterns every"
            hint="Days. Each run reads this desk's resolved tickets through a model, so it costs money. Switch mining on or off under Features."
          >
            <Input
              type="number"
              min={1}
              max={30}
              value={String(draft.miningEveryDays)}
              onChange={(e) => setDraft({ ...draft, miningEveryDays: Number(e.target.value) })}
              aria-label="Mining interval in days"
            />
          </Field>
          {/* The bound on what a run costs, and the more honest question:
              "problems the desk keeps solving" means lately. A problem that
              stopped two years ago is not work anybody is still doing. */}
          <Field
            label="Only consider tickets resolved in the last"
            hint="Days. Bounds what each run costs, and lets a pattern that stopped recurring drop off the list. Long to start with — the imported history is the evidence."
          >
            <Input
              type="number"
              min={7}
              max={3650}
              value={String(draft.miningLookbackDays)}
              onChange={(e) => setDraft({ ...draft, miningLookbackDays: Number(e.target.value) })}
              aria-label="Mining lookback in days"
            />
          </Field>
        </div>
      </Panel>

      <SuggestionsPanel
        draft={draft}
        setDraft={setDraft}
        defaults={q.data!.defaults}
        onSave={save}
        canSave={dirty && !clash}
        saving={write.isPending}
      />
    </div>
  );
}

/**
 * What gets offered, to whom.
 *
 * TWO AUDIENCES, TWO BARS. An agent can weigh a middling article; a
 * requester follows it alone. One bar served both, which meant setting it
 * for one of them and living with it for the other.
 *
 * Every blank field shows what blank MEANS. "Deployment default" told a
 * person nothing about why a 74% match had vanished; "75" does.
 */
function SuggestionsPanel({
  draft,
  setDraft,
  defaults,
  onSave,
  canSave,
  saving,
}: {
  draft: Knowledge;
  setDraft: (k: Knowledge) => void;
  defaults: { suggestMinPercent: number; judgeMarginPercent: number };
  onSave: () => void;
  canSave: boolean;
  saving: boolean;
}) {
  // Empty clears a field back to its default. `Number('')` is 0, which for
  // the bar would silently mean "offer anything".
  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
  const agentBar = draft.suggestMinPercent ?? defaults.suggestMinPercent;
  const agentMargin = draft.suggestJudgeMarginPercent ?? defaults.judgeMarginPercent;

  const columns = [
    {
      key: 'agent',
      title: 'Agents',
      icon: <Lock />,
      blurb: 'Suggested solutions on the ticket screen.',
      bar: draft.suggestMinPercent,
      barPlaceholder: `${defaults.suggestMinPercent} (default)`,
      setBar: (v: number | undefined) => setDraft({ ...draft, suggestMinPercent: v }),
      margin: draft.suggestJudgeMarginPercent,
      marginPlaceholder: `${defaults.judgeMarginPercent} (default)`,
      setMargin: (v: number | undefined) => setDraft({ ...draft, suggestJudgeMarginPercent: v }),
    },
    {
      key: 'requester',
      title: 'Requesters',
      icon: <BookOpen />,
      blurb: "The portal and the intake assistant's search.",
      bar: draft.requesterSuggestMinPercent,
      barPlaceholder: `${agentBar} (same as agents)`,
      setBar: (v: number | undefined) => setDraft({ ...draft, requesterSuggestMinPercent: v }),
      margin: draft.requesterSuggestJudgeMarginPercent,
      marginPlaceholder: `${agentMargin} (same as agents)`,
      setMargin: (v: number | undefined) =>
        setDraft({ ...draft, requesterSuggestJudgeMarginPercent: v }),
    },
  ];

  return (
    <Panel
      title="Suggestions"
      subtitle="How close a knowledgebase article has to be before it is offered"
      action={
        <Button onClick={onSave} disabled={!canSave} loading={saving}>
          Save
        </Button>
      }
      footer={
        <p className="text-2xs leading-relaxed text-ink-tertiary">
          The <strong className="text-ink-secondary">bar</strong> is the match percentage the
          suggestion card shows. Articles under it are not offered. Within the{' '}
          <strong className="text-ink-secondary">margin</strong> under the bar, the model reads
          the article and offers it only if it answers the request &mdash; similarity finds the
          neighbours but cannot tell the right one from a lookalike. Without a model the bar is
          exact. Leave a field blank to use what it shows. Answering a requester&rsquo;s email
          is separate: the assistant reads its own shortlist and decides from the articles.
        </p>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {columns.map((c) => (
          <section key={c.key} className="space-y-2.5">
            <header className="flex items-start gap-2">
              <span aria-hidden className="mt-0.5 text-ink-tertiary [&>svg]:h-3.5 [&>svg]:w-3.5">
                {c.icon}
              </span>
              <div>
                <h3 className="text-xs font-semibold text-ink">{c.title}</h3>
                <p className="text-2xs leading-snug text-ink-tertiary">{c.blurb}</p>
              </div>
            </header>
            <Field label="Offer a match of at least" hint="Percent.">
              <Input
                type="number"
                min={0}
                max={100}
                placeholder={c.barPlaceholder}
                value={c.bar === undefined ? '' : String(c.bar)}
                onChange={(e) => c.setBar(num(e.target.value))}
                aria-label={`${c.title}: minimum match percent`}
              />
            </Field>
            <Field
              label="Let the model read this far under it"
              hint="Percentage points. 0 means only the bar decides."
            >
              <Input
                type="number"
                min={0}
                max={50}
                placeholder={c.marginPlaceholder}
                value={c.margin === undefined ? '' : String(c.margin)}
                onChange={(e) => c.setMargin(num(e.target.value))}
                aria-label={`${c.title}: model margin in points`}
              />
            </Field>
          </section>
        ))}
      </div>
    </Panel>
  );
}
